<script lang="ts">
    import MeetingCard from '../components/MeetingCard.svelte';
    import MeetingRoster from '../components/MeetingRoster.svelte';
    import {error, meetings, user} from '../stores';
    import {isUserAdmin, sameDayAtAndover, toAndoverTime, validInputStrings} from '../util';
    import {Button, Icon, Dialog, Card, CardTitle, CardText, CardActions} from 'svelte-materialify';
    import {Row, Col, TextField} from 'svelte-materialify/src';
    import ExpansionPanels, {ExpansionPanel} from 'svelte-materialify/src/components/ExpansionPanels';
    import {addMeeting} from '../firestore';
    import type {Meeting} from '../firestore';
    import MeetingEditor from "../components/MeetingEditor.svelte";

    let now = new Date();
    setInterval(() => now = new Date(), 1000 * 60 * 60); // hourly

    function splitByRelativeTime(meetings: Meeting[], now: Date): [Meeting[], Meeting[], Meeting[]] {
        const today: Meeting[] = [];
        const past: Meeting[] = [];
        const future: Meeting[] = [];
        for (const mtg of meetings) {
            if (sameDayAtAndover(mtg.startTime, now)) {
                today.push(mtg);
            } else if (mtg.endTime.getTime() < now.getTime()) {
                past.push(mtg);
            } else {
                future.push(mtg);
            }
        }
        return [past, today, future];
    }

    $: [meetingsPast, meetingsToday, meetingsFuture] = splitByRelativeTime($meetings, now);
    $: admin = $user != null && isUserAdmin($user);

    let showAddDialog = false;
    let nmID: string | null = null;
    let nmStart: string | null = null;
    let nmEnd: string | null = null;
    let nmCap: number | null = 12;
    $: nmValid = validInputStrings([nmID, nmStart, nmEnd]) && nmCap != null && new Date(nmEnd).getTime() - new Date(nmStart).getTime() > 0;

    function nmDiscard() {
        nmID = nmStart = nmEnd = null;
        nmCap = 12;
        showAddDialog = false;
    }

    function nmSubmit() {
        addMeeting(nmID, toAndoverTime(new Date(nmStart)), toAndoverTime(new Date(nmEnd)), nmCap)
            .then(() => showAddDialog = false)
            .catch(error.set);
    }

    let editor;
</script>

<div class="meetings">
    {#if admin}
        <Button size="large" rounded class="primary-color" on:click={() => showAddDialog = true}>
            <Icon class="material-icons">add</Icon>
            &nbsp;
            New Meeting
        </Button>
    {/if}
    <div class="meetings-today pa-4">
        {#each meetingsToday as meeting (meeting.id)}
            <div class="meeting">
                <MeetingCard value={meeting} />
            </div>
        {:else}
            <i>No meetings today</i>
        {/each}
    </div>
    <div class="other-meetings">
        <Row>
            <Col class="pa-5">
                <h4 class="pb-3">Past</h4>
                <ExpansionPanels>
                    {#each meetingsPast as meeting (meeting.id)}
                        <ExpansionPanel>
                            <span slot="header">{meeting.id}</span>
                            <MeetingRoster value={meeting} />
                        </ExpansionPanel>
                    {/each}
                </ExpansionPanels>
            </Col>
            <Col class="pa-5">
                <h4 class="pb-3">Upcoming</h4>
                <ExpansionPanels>
                    {#each meetingsFuture as meeting (meeting.id)}
                        <ExpansionPanel>
                            <span slot="header">{meeting.id}</span>
                            <div class="d-block">
                                <MeetingRoster value={meeting} showAttendance={false} />
                                {#if admin}
                                    <Button class="primary-text" text on:click={() => editor.open(meeting.id)}>Edit</Button>
                                {/if}
                            </div>
                        </ExpansionPanel>
                    {/each}
                </ExpansionPanels>
            </Col>
        </Row>
    </div>
</div>

<style>
    .meetings {
        max-width: 1000px;
        padding: 1rem;
        margin: 0 auto;
    }
    .meetings > div {
        margin: 0.8rem;
    }
    .nm-inputs td {
        padding: 0.25rem;
    }
</style>

<Dialog bind:active={showAddDialog}>
    <Card>
        <CardTitle>New Meeting</CardTitle>
        <CardText>
            <table style="width: 100%" class="nm-inputs">
                <tr>
                    <td><b>Meeting ID</b></td>
                    <td><TextField dense required bind:value={nmID}>Meeting ID</TextField></td>
                </tr>
                <tr>
                    <td><b>Start Time</b></td>
                    <td><input type="datetime-local" name="nm-start" id="nm-start" placeholder="Start Time" required bind:value={nmStart}></td>
                </tr>
                <tr>
                    <td><b>End Time</b></td>
                    <td><input type="datetime-local" name="nm-end" id="nm-end" required bind:value={nmEnd}></td>
                </tr>
                <tr>
                    <td><b>Capacity</b></td>
                    <td><input type="number" name="nm-cap" id="nm-cap" bind:value={nmCap} required min="1" max="30" placeholder="Capacity (# people)"></td>
                </tr>
            </table>
        </CardText>
        <CardActions>
            <Button text disabled={nmValid ? undefined : true} on:click={nmSubmit}>
                <Icon class="material-icons">send</Icon>&nbsp
                Submit
            </Button>
            <Button text class="text-secondary" on:click={nmDiscard}>
                <Icon class="material-icons">delete</Icon>&nbsp
                Discard
            </Button>
        </CardActions>
    </Card>
</Dialog>

<MeetingEditor bind:this={editor} />